import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import {
    FormControl,
    InputLabel,
    FormHelperText,
    createTheme,
    ThemeProvider,
    StyledEngineProvider,
    adaptV4Theme,
    Theme,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import clsx from 'clsx'
import { $Gainsboro, $Gray75, $WhiteSmoke } from '@Styles/colors'

declare module '@mui/styles/defaultTheme' {
    // eslint-disable-next-line @typescript-eslint/no-empty-interface
    interface DefaultTheme extends Theme {}
}

const materialTheme = createTheme(
    adaptV4Theme({
        overrides: {
            MuiInputLabel: {
                root: {
                    fontSize: '14px',
                    color: $Gray75,
                    margin: 0,
                    '&$focused': {
                        color: $Gray75,
                    },
                },
            },
        },
    })
)

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        border: `1px solid #E2E7EB`,
        overflow: 'hidden',
        borderRadius: 10,
        backgroundColor: $WhiteSmoke,
        // height: 50,
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        '&:hover': {
            backgroundColor: '#fff',
        },
        '& .MuiFilledInput-input': {
            padding: '21px 12px 6px',
            fontWeight: 'bold',
        },
        '& .MuiSelect-iconFilled': {
            right: 13,
        },
        '& .Mui-disabled': {
            '&:hover': {
                cursor: 'no-drop',
            },
            background: $WhiteSmoke,
            // backgroundColor: '#E0E0E0',
        },
        '& svg.Mui-disabled': {
            display: 'none',
        },
    },
    formControl: {
        minWidth: 120,
        // height: 50,

        '& .MuiFormHelperText-root': {
            fontStyle: 'italic',
        },
    },
    menuProps: {
        borderRadius: '10px',
        maxHeight: '500px',
    },
    inputLabel: {
        fontSize: '14px',
        color: $Gray75,
        '&.Mui-focused': {
            color: '#757575 !important',
        },
    },

    inputBox: {
        textAlign: 'left',
        fontSize: 16,
    },
    listItem: {
        padding: '10px 20px',
        fontSize: 12,
        fontWeight: 600,

        '& .divider': {
            borderBottom: `0.5px solid ${$Gainsboro}`,
            position: 'absolute',
            width: '100%',
            top: 27,
        },
    },
}))

interface IBaseSelect {
    value?: any
    onChange?: (value: any) => void
    options?: any[]
    label?: string
    fullWidth?: boolean
    id?: string
    optionCentered?: boolean
    defaultValue?: any
    className?: string
    error?: any
    helperText?: string
    inputRef?: any
    disabled?: boolean
    readOnly?: boolean
}

const BaseSelect = (props: IBaseSelect) => {
    // core section
    const {
        value,
        onChange,
        options,
        label = 'Label',
        fullWidth = false,
        id = 'custom-id',
        optionCentered = true,
        defaultValue,
        className,
        error,
        helperText,
        inputRef,
        disabled = false,
        readOnly = false,
    } = props

    const classes = useStyles()

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={materialTheme}>
                <FormControl
                    variant="filled"
                    className={clsx(
                        classes.formControl,
                        fullWidth && 'w-100',
                        className
                    )}
                >
                    <InputLabel
                        className={classes.inputLabel}
                        id={`${id}-label`}
                    >
                        {label}
                    </InputLabel>
                    <Select
                        disabled={disabled}
                        fullWidth={fullWidth}
                        labelId={`${id}-label`}
                        id={id}
                        value={value}
                        onChange={onChange}
                        disableUnderline
                        className={classes.root}
                        IconComponent={ExpandMoreIcon}
                        defaultValue={defaultValue}
                        error={error}
                        MenuProps={{
                            classes: { paper: classes.menuProps },
                            // getContentAnchorEl: null,
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'center',
                            },
                            MenuListProps: {
                                disablePadding: true,
                            },
                        }}
                        inputRef={inputRef}
                        inputProps={{
                            classes: { root: classes.inputBox },
                        }}
                        readOnly={readOnly}
                    >
                        {options?.map((option, index) => (
                            <MenuItem
                                value={option.value}
                                key={`${option.value}_select_optins`}
                                className={clsx(
                                    classes.listItem,
                                    optionCentered && 'text-center'
                                )}
                            >
                                <div className="position-relative w-100">
                                    {option?.name === 'None' ? (
                                        <em>{option?.name}</em>
                                    ) : (
                                        option?.name
                                    )}
                                    {options.length - 1 !== index && (
                                        <div className="divider" />
                                    )}
                                </div>
                            </MenuItem>
                        ))}
                    </Select>
                    {helperText && (
                        <FormHelperText error>{helperText}</FormHelperText>
                    )}
                </FormControl>
            </ThemeProvider>
        </StyledEngineProvider>
    )
}

export default BaseSelect
